import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/axiosInstance';
import '../../styles/modal.css';
import { toast } from 'react-toast';
import ProjectKeyField from './ProjectKeyField';

const RequirementUploadForm = ({ project, onCancel, onSuccess, variant = 'modal' }) => {
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [selectedProjectKey, setSelectedProjectKey] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [projectKey, setProjectKey] = useState('');
  const [projectKeyError, setProjectKeyError] = useState('');
  const [fetchedProjectKey, setFetchedProjectKey] = useState('');
  const [keySaved, setKeySaved] = useState(false);

  const isPage = variant === 'page';

  const getAuthData = () => {
    const authDataString = localStorage.getItem('user');
    if (!authDataString) {
      throw new Error('User not authenticated - no auth data found');
    }
    const authData = JSON.parse(authDataString);
    if (!authData.accessToken) {
      throw new Error('Access token not found in auth data');
    }
    return authData;
  };

  useEffect(() => {
    if (!project?.id) return;

    const fetchJiraProjects = async () => {
      try {
        const authData = getAuthData();
        setUserEmail(authData.username);

        const source = project?.originalData?.integration_source;

        const response = await axiosInstance.get(
          `/jira_data/get_jira_project_details?project_id=${project.id}`,
        );

        const returnedKey = response?.project_key || '';
        setFetchedProjectKey(returnedKey);

        if (source === 1 || source === 2) {
          setSelectedProjectKey(returnedKey);
        }
        if (source === 3 && returnedKey) {
          setSelectedProjectKey(returnedKey);
        }
      } catch (err) {
        setError('Failed to fetch JIRA projects. Please try again.');
        console.error('Error fetching JIRA projects:', err);
      }
    };

    fetchJiraProjects();

    return () => {
      setFiles([]);
      setUploadProgress(0);
      setIsUploading(false);
      setError(null);
      setProjectKey('');
    };
  }, [project]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isUploading) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain',
    ];

    const validFiles = [];
    droppedFiles.forEach((droppedFile, i) => {
      if (validTypes.includes(droppedFile.type)) {
        validFiles.push({
          id: `${Date.now()}_${i}`,
          file: droppedFile,
          name: droppedFile.name,
          size: droppedFile.size,
        });
      } else {
        setError(`${droppedFile.name} is not a valid file. Please upload PDF, DOCX`);
      }
    });

    if (validFiles.length > 0) {
      setFiles((prev) => [...prev, ...validFiles]);
      setError(null);
    }
  }, [isUploading]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain',
    ];

    const validFiles = [];
    selectedFiles.forEach((selectedFile, i) => {
      if (validTypes.includes(selectedFile.type)) {
        validFiles.push({
          id: `${Date.now()}_${i}`,
          file: selectedFile,
          name: selectedFile.name,
          size: selectedFile.size,
        });
      } else {
        setError(`${selectedFile.name} is not a valid file type.`);
      }
    });

    if (validFiles.length > 0) {
      setFiles((prev) => [...prev, ...validFiles]);
      setError(null);
    }
  };

  const handleRemoveFile = (fileId) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleCancel = () => {
    setProjectKey('');
    onCancel?.();
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }

    if (!selectedProjectKey) {
      setError('Please select a JIRA project');
      return;
    }

    if (!userEmail) {
      setError('Please enter your email address');
      return;
    }

    if (!project || !project.id) {
      setError('Project information is not available');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      files.forEach((fileObj) => formData.append('files', fileObj.file));
      formData.append('project_key', selectedProjectKey);
      formData.append('email', userEmail);

      const response = await axiosInstance.post(
        '/ai_test_case/generate-epic-tasks/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const responseData = response?.data || response;
      // toast.success(responseData.message || 'Requirements processing started successfully!');

      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 10;
        setUploadProgress(progress);

        if (progress >= 90) {
          clearInterval(progressInterval);
        }
      }, 500);

      setTimeout(() => {
        clearInterval(progressInterval);
        setUploadProgress(100);
        setIsUploading(false);
        toast.success('Requirements processed successfully!');
        setProjectKey('');
        if (onSuccess) {
          onSuccess();
        } else {
          onCancel?.();
        }
      }, 3000);
    } catch (uploadError) {
      console.error('Upload error:', uploadError);
      setError(uploadError.response?.data?.message || uploadError.message || 'An error occurred while processing the file.');
      toast.error(`Failed to upload requirements: ${uploadError.message}`);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const source = project?.originalData?.integration_source;
  const showReadOnlyKeyField = source === 1 || source === 2 || (source === 3 && !!fetchedProjectKey);
  const showEditableKeyField = (source === 3 && !fetchedProjectKey) || !source;
  const integrationLabel = project?.type === 'project_pulse' ? 'WorkXiq' : 'Jira';

  const bodyClassName = isPage ? 'space-y-6' : 'modal-body';
  const footerClassName = isPage
    ? 'flex items-center justify-end gap-3 pt-6 border-t border-gray-200'
    : 'modal-footer';

  const renderFileList = () => {
    if (files.length === 0 || isUploading) return null;

    return (
      <div className={isPage ? 'mt-4 space-y-3' : undefined} style={isPage ? undefined : { marginTop: '1rem', display: 'grid', gap: '0.75rem' }}>
        {files.map((fileObj) => {
          const isPdf = fileObj.name.endsWith('.pdf');
          return (
            <div
              key={fileObj.id}
              className={isPage
                ? 'flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md'
                : undefined}
              style={isPage ? undefined : {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.75rem 1rem',
                backgroundColor: 'rgba(34,197,94,0.1)',
                borderRadius: '6px',
                border: '1px solid rgba(34,197,94,0.3)',
              }}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="text-xl">{isPdf ? '📕' : '📗'}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{fileObj.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {(fileObj.size / 1024).toFixed(2)} KB • {isPdf ? 'PDF' : 'DOCX'}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile(fileObj.id);
                }}
                className={isPage
                  ? 'shrink-0 ml-3 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors duration-200'
                  : undefined}
                style={isPage ? undefined : {
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.35rem 0.65rem',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                ✕ Remove
              </button>
            </div>
          );
        })}

        <div className={isPage
          ? 'text-sm text-green-700 p-2 bg-green-50 rounded-md'
          : undefined}
          style={isPage ? undefined : {
            fontSize: '0.875rem',
            color: '#22c55e',
            padding: '0.5rem',
            backgroundColor: 'rgba(34,197,94,0.1)',
            borderRadius: '4px',
          }}
        >
          ✓ {files.length} file(s) ready to upload
        </div>
      </div>
    );
  };

  const renderUploadArea = () => (
    <div className={isPage ? '' : 'upload-container'}>
      <div
        className={isPage
          ? `border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : files.length > 0
                  ? 'border-green-400 bg-green-50'
                  : 'border-gray-300 hover:border-blue-400'
            }`
          : `file-upload-area ${isDragging ? 'dragging' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="requirement-file"
          onChange={handleFileChange}
          multiple
          accept=".pdf,.docx"
          disabled={isUploading}
          className="hidden"
          style={isPage ? undefined : { display: 'none' }}
        />
        <label
          htmlFor="requirement-file"
          className={isPage
            ? `flex flex-col items-center gap-3 cursor-pointer ${isUploading ? 'cursor-not-allowed opacity-70' : ''}`
            : `file-upload-label ${isUploading ? 'disabled' : ''}`}
        >
          {isUploading ? (
            <div className="flex flex-col items-center w-full gap-2">
              <div className="flex items-center gap-2 text-gray-700">
                <i className="fas fa-spinner fa-spin" />
                <span>Processing Requirements...</span>
              </div>
              <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span className="text-sm text-gray-500">{Math.round(uploadProgress)}%</span>
            </div>
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={isPage ? 'text-blue-600' : undefined}
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <div className={isPage ? 'text-sm font-medium text-gray-700' : 'file-name'}>
                {files.length > 0
                  ? `${files.length} file(s) selected`
                  : isDragging ? 'Drop files here' : 'Choose files or drag them here'}
              </div>
              <span className={isPage ? 'text-xs text-gray-500' : 'file-types'}>
                Supported formats: PDF, DOCX
              </span>
            </>
          )}
        </label>
      </div>

      {renderFileList()}

      {isUploading && !isPage && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <span>{uploadProgress}%</span>
        </div>
      )}

      {error && (
        <div className={isPage
          ? 'flex items-center gap-2 mt-4 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md'
          : 'upload-error'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className={bodyClassName}>
        {!isPage && (
          <p className="modal-description">
            Upload a requirements document for <strong>{project ? project.name : 'this project'}</strong> to automatically create EPICS and Stories in {integrationLabel}.
            The system will analyze the document and extract requirements specific to this project.
          </p>
        )}

        {showReadOnlyKeyField && (
          <div className={isPage ? '' : 'form-group'}>
            <label
              htmlFor="jira-project"
              className={isPage ? 'block text-sm font-medium text-gray-700 mb-1' : undefined}
            >
              Your JIRA Project ID:
            </label>
            <input
              type="text"
              id="jira-project"
              value={selectedProjectKey}
              readOnly
              className={isPage
                ? 'w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed'
                : undefined}
              style={isPage ? undefined : { backgroundColor: '#f5f5f5' }}
            />
          </div>
        )}

        {showEditableKeyField && (
          <ProjectKeyField
            value={projectKey}
            onChange={setProjectKey}
            onErrorChange={setProjectKeyError}
            error={projectKeyError}
            setUploadProjectKey={setSelectedProjectKey}
            projectId={project?.id}
            onKeySaved={setKeySaved}
            projectName={project?.name || 'Project'}
            variant={isPage ? 'page' : 'wizard'}
          />
        )}

        <div className={isPage ? '' : 'form-group'}>
          <label
            htmlFor="user-email"
            className={isPage ? 'block text-sm font-medium text-gray-700 mb-1' : undefined}
          >
            Your Email:
          </label>
          <input
            type="email"
            id="user-email"
            value={userEmail}
            readOnly
            className={isPage
              ? 'w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed'
              : undefined}
            style={isPage ? undefined : { backgroundColor: '#f5f5f5' }}
          />
        </div>

        {isPage ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Requirements Document <span className="text-red-500">*</span>
            </label>
            {renderUploadArea()}
          </div>
        ) : (
          renderUploadArea()
        )}

        <div className={isPage
          ? 'bg-gray-50 border border-gray-200 rounded-lg p-4'
          : 'upload-info'}
        >
          <h3 className={isPage ? 'text-sm font-semibold text-gray-900 mb-3' : undefined}>
            How it works:
          </h3>
          <ol className={isPage ? 'text-sm text-gray-600 space-y-2 list-decimal list-inside' : undefined}>
            <li>Upload your requirements document (PDF, DOCX)</li>
            <li>The system will analyze the document and identify requirements</li>
            <li>EPICS and Stories will be automatically created in {integrationLabel}</li>
            <li>You will receive an email notification when processing is complete</li>
          </ol>
        </div>
      </div>

      <div className={footerClassName}>
        <button
          type="button"
          className={isPage
            ? 'px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
            : 'modal-cancel-btn'}
          onClick={handleCancel}
          disabled={isUploading}
        >
          Cancel
        </button>
        <button
          type="button"
          className={isPage
            ? 'inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors duration-200'
            : 'modal-action-btn'}
          onClick={handleUpload}
          disabled={files.length === 0 || isUploading || !selectedProjectKey || !userEmail || (showEditableKeyField && !keySaved)}
        >
          {isUploading ? 'Uploading...' : 'Upload and Process'}
        </button>
      </div>
    </>
  );
};

export default RequirementUploadForm;
